import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

// A plant-local calendar date, as the backend publishes and accepts it.
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isCalendarDate(value) {
	if (!DATE_PATTERN.test(value || "")) return false;
	const parsed = new Date(`${value}T12:00:00Z`);
	return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * The calendar date `days` away from `dateKey`.
 *
 * Stepped from a midday anchor in UTC, which is the same arithmetic the backend does: these are
 * plain calendar dates belonging to the plant, and the browser's own zone must never shift them.
 * A midday anchor also keeps a DST day from landing back on its own date.
 */
export function shiftDate(dateKey, days) {
	return new Date(Date.parse(`${dateKey}T12:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
}

// Share the selected day across tabs and links. No date parameter means the plant's
// today, resolved by the server. Invalid calendar dates are treated as absent; the raw
// parameter remains available so the picker can offer a reset even after an API rejection.
export default function useDaySelection() {
	const [searchParams, setSearchParams] = useSearchParams();
	const requested = searchParams.get("date");
	const date = isCalendarDate(requested) ? requested : null;

	const setDate = useCallback((next) => {
		const params = new URLSearchParams(searchParams);
		if (isCalendarDate(next)) params.set("date", next); else params.delete("date");
		// Replaced rather than pushed: stepping a day at a time is browsing one panel, not
		// navigating, and it should not bury the page the supervisor arrived from under twenty
		// entries of back history.
		setSearchParams(params, { replace: true });
	}, [searchParams, setSearchParams]);

	return { date, setDate, requestedDate: requested };
}
